import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Dauerhaft sichtbarer Handlungsaufruf auf Mobile: eine kurze Schaltfläche.
 * Blendet sich aus, sobald ein Eingabefeld fokussiert ist oder ein Formularblock
 * im Sichtbereich steht, damit weder Felder noch Erfolgsmeldungen verdeckt werden.
 * Rechts bleibt fest Platz für das Chat-Widget.
 */
export function StickyCta({ targetId }: { targetId?: string }) {
  const [typing, setTyping] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const check = () => {
      const el = document.activeElement;
      setTyping(!!el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
    };
    document.addEventListener("focusin", check);
    document.addEventListener("focusout", check);
    return () => {
      document.removeEventListener("focusin", check);
      document.removeEventListener("focusout", check);
    };
  }, []);

  // Nach jeder Navigation neu beobachten: die Formularblöcke der neuen Seite.
  useEffect(() => {
    setFormVisible(false);
    const targets = Array.from(
      document.querySelectorAll<HTMLElement>("form, [data-lead-block]"),
    );
    if (!targets.length) return;
    const seen = new Map<Element, boolean>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => seen.set(e.target, e.isIntersecting));
        setFormVisible(Array.from(seen.values()).some(Boolean));
      },
      { threshold: 0.12 },
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, [pathname]);

  if (typing || formVisible) return null;

  const cls =
    "shadow-signal inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-primary px-5 py-3.5 text-sm font-semibold text-primary-foreground";

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 px-4 py-3 backdrop-blur-md lg:hidden"
      style={{
        paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
      }}
    >
      {targetId ? (
        <a href={`#${targetId}`} className={cls}>
          Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
        </a>
      ) : (
        <Link to="/kontakt" className={cls}>
          Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}
